import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Crown, TrendingUp, Gift, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const SpendingReports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (user?.subscription_tier !== 'elite') {
      toast.error('Elite subscription required');
      navigate('/subscription');
      return;
    }
    fetchReport(selectedYear);
  }, [user]);

  const fetchReport = async (year) => {
    try {
      setLoading(true);
      const response = await api.get(`/reports/spending?year=${year}`);
      setReport(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        navigate('/subscription');
      } else {
        toast.error('Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(parseInt(year));
    fetchReport(parseInt(year));
  };

  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50" data-testid="spending-reports-page">
      <header className="bg-white shadow-sm border-b border-red-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">
              <span className="gradient-text">📊 Spending Reports</span>
            </h1>
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <Crown className="w-4 h-4" />
              Elite Feature
            </div>
          </div>
          <div className="w-48">
            <Select value={selectedYear.toString()} onValueChange={handleYearChange}>
              <SelectTrigger data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : !report ? (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <p className="text-gray-600">No data available</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="w-5 h-5 text-red-600" />
                    Total Spent
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-red-600">
                    {formatCurrency(report.total_spent)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">in {selectedYear}</p>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Gift className="w-5 h-5 text-green-600" />
                    Total Gifts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-600">
                    {report.total_gifts}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">gifts purchased</p>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Average Price
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-600">
                    {formatCurrency(report.average_price)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">per gift</p>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Breakdown */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>📅 Monthly Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(report.by_month).length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No purchases this year</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(report.by_month)
                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                      .map(([month, data]) => (
                        <div key={month} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex-1">
                            <p className="font-semibold">{monthNames[parseInt(month) - 1]}</p>
                            <p className="text-sm text-gray-600">{data.count} gifts</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-red-600">{formatCurrency(data.total)}</p>
                            <p className="text-sm text-gray-600">
                              Avg: {formatCurrency(data.total / data.count)}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* By Recipient */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>👥 Spending by Recipient</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(report.by_recipient).length === 0 ? (
                  <p className="text-gray-600 text-center py-8">No data available</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(report.by_recipient)
                      .sort(([, a], [, b]) => b.total - a.total)
                      .map(([recipient, data]) => (
                        <div key={recipient} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex-1">
                            <p className="font-semibold">{recipient}</p>
                            <p className="text-sm text-gray-600">{data.count} gifts</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-red-600">{formatCurrency(data.total)}</p>
                            <p className="text-sm text-gray-600">
                              Avg: {formatCurrency(data.total / data.count)}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default SpendingReports;
